import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

interface NotificationContextType {
  hasNewNotification: boolean;
  refreshNotifications: () => void;
  clearNewNotificationFlag: () => void;
  registerRefreshCallback: (_callback: (_data?: any) => void) => () => void;
  unregisterRefreshCallback: (_callback: (_data?: any) => void) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [hasNewNotification, setHasNewNotification] = useState(false);
  const refreshCallbacks = useRef<Set<() => void>>(new Set());

  // Setup message listener for push notifications
  useEffect(() => {
    const handlePushNotification = (event: MessageEvent) => {
      if (event.data && event.data.type === 'PUSH_NOTIFICATION_RECEIVED') {
        setHasNewNotification(true);

        refreshCallbacks.current.forEach((callback) => {
          try {
            (callback as any)(event.data.data);
          } catch (error) {
            console.error('Error calling notification refresh callback:', error);
          }
        });
      }
    };

    navigator.serviceWorker.addEventListener('message', handlePushNotification);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handlePushNotification);
    };
  }, []);

  // Register a refresh callback
  const registerRefreshCallback = (callback: (_data?: any) => void) => {
    refreshCallbacks.current.add(callback);

    // Return cleanup function
    return () => {
      refreshCallbacks.current.delete(callback);
    };
  };

  // Unregister a refresh callback
  const unregisterRefreshCallback = (callback: (_data?: any) => void) => {
    refreshCallbacks.current.delete(callback);
  };

  // Refresh all notifications
  const refreshNotifications = () => {
    refreshCallbacks.current.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error('Error calling notification refresh callback:', error);
      }
    });
  };

  // Clear new notification flag
  const clearNewNotificationFlag = () => {
    setHasNewNotification(false);
  };

  const value: NotificationContextType = {
    hasNewNotification,
    refreshNotifications,
    clearNewNotificationFlag,
    registerRefreshCallback,
    unregisterRefreshCallback,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

// Export the register/unregister functions for components to use
export { NotificationProvider as default };
